import { useEffect, useMemo, useState } from 'react'
import type { PmmRepository } from '../data/repository'
import {
  ONBOARDING_ANSWER_STATE_OPTIONS,
  ONBOARDING_CHAPTERS,
  ONBOARDING_INTRO_STORY,
  answerStateOf,
  createOnboardingResponse,
  fieldsForChapter,
  onboardingAiPreview,
  classifyOnboardingSafety,
  validateChapter,
  type OnboardingAnswerState,
  type OnboardingFieldDefinition,
  type OnboardingResponse,
  type OnboardingSession,
  type OnboardingValue,
} from '../domain/onboarding'
import { PartnerWhatsAppDraft } from './PartnerWhatsAppDraft'
import { PartnerConnectionCard } from './PartnerConnectionCard'

interface OnboardingWorkspaceProps {
  repository: PmmRepository
  onComplete: () => void
}

export function onboardingPersistenceErrorMessage(cause: unknown): string {
  const error = cause as { code?: unknown; name?: unknown; message?: unknown } | null
  const fingerprint = [error?.code, error?.name, error?.message].map((value) => String(value ?? '')).join(' ').toLowerCase()
  if (fingerprint.includes('conflict') || fingerprint.includes('aborted')) {
    return 'This baseline changed in another session. Refresh before continuing so no answer is overwritten. Reference: PMM-ONBOARDING-CONFLICT.'
  }
  if (fingerprint.includes('appcheck') || fingerprint.includes('app-check') || fingerprint.includes('recaptcha') || fingerprint.includes('attestation') || fingerprint.includes('initial-throttle') || fingerprint.includes('unauthenticated')) {
    return 'This device could not complete secure save verification. Your answers remain on this screen. Reference: PMM-ONBOARDING-VERIFY.'
  }
  if (fingerprint.includes('network') || fingerprint.includes('unavailable') || fingerprint.includes('failed to fetch') || fingerprint.includes('content security policy')) {
    return 'PMM could not reach the secure save service. Your answers remain on this screen. Reference: PMM-ONBOARDING-NETWORK.'
  }
  if (fingerprint.includes('invalid-argument') || fingerprint.includes('failed-precondition')) {
    return 'One answer could not be safely validated. Review this chapter and try once more. Reference: PMM-ONBOARDING-VALIDATION.'
  }
  return 'This chapter could not be saved. Your answers remain on this screen. Reference: PMM-ONBOARDING-SERVICE.'
}

function responseValue(response: OnboardingResponse | undefined): OnboardingValue | '' {
  return response?.value ?? ''
}

function FieldControl({
  definition,
  response,
  error,
  onChange,
  onAiUseChange,
  onAnswerStateChange,
}: {
  definition: OnboardingFieldDefinition
  response?: OnboardingResponse
  error?: string
  onChange: (value: OnboardingValue) => void
  onAiUseChange: (enabled: boolean) => void
  onAnswerStateChange: (state: OnboardingAnswerState) => void
}) {
  const inputId = `onboarding-${definition.id.replaceAll('.', '-')}`
  const value = responseValue(response)
  const answerState = answerStateOf(response)
  const textUnavailable = ['never_explored', 'temporarily_inaccessible', 'prefer_not_to_answer', 'not_applicable'].includes(answerState)
  const describedBy = `${inputId}-help${error ? ` ${inputId}-error` : ''}`

  let control
  if (definition.kind === 'boolean') {
    control = (
      <div className="onboarding-choice-row" role="radiogroup" aria-labelledby={`${inputId}-label`}>
        {[{ label: 'Yes', value: true }, { label: 'No', value: false }].map((choice) => (
          <label className={value === choice.value ? 'choice-card selected' : 'choice-card'} key={choice.label}>
            <input type="radio" name={inputId} checked={value === choice.value} onChange={() => onChange(choice.value)} />
            <span>{choice.label}</span>
          </label>
        ))}
      </div>
    )
  } else if (definition.kind === 'select') {
    control = (
      <select id={inputId} value={String(value)} onChange={(event) => onChange(event.target.value)} aria-describedby={describedBy}>
        <option value="">Choose one</option>
        {definition.options?.map((entry) => <option value={entry.value} key={entry.value}>{entry.label}</option>)}
      </select>
    )
  } else if (definition.kind === 'multiselect') {
    const selected = Array.isArray(value) ? value : []
    control = (
      <div className="onboarding-multiselect" role="group" aria-labelledby={`${inputId}-label`}>
        {definition.options?.map((entry) => (
          <label className={selected.includes(entry.value) ? 'choice-card selected' : 'choice-card'} key={entry.value}>
            <input type="checkbox" checked={selected.includes(entry.value)} onChange={(event) => onChange(event.target.checked ? [...selected, entry.value] : selected.filter((item) => item !== entry.value))} />
            <span>{entry.label}</span>
          </label>
        ))}
      </div>
    )
  } else if (definition.kind === 'textarea') {
    control = <textarea id={inputId} value={String(value)} maxLength={definition.maxLength} rows={4} disabled={textUnavailable} placeholder={textUnavailable ? 'No written answer is needed for the state you selected.' : 'Write only what feels true enough today.'} onChange={(event) => onChange(event.target.value)} aria-describedby={describedBy} />
  } else if (definition.kind === 'number') {
    control = <input id={inputId} type="number" min={definition.min} max={definition.max} value={value === '' ? '' : Number(value)} onChange={(event) => onChange(event.target.value === '' ? '' : Number(event.target.value))} aria-describedby={describedBy} />
  } else if (definition.kind === 'scale') {
    const numericValue = typeof value === 'number' ? value : definition.min ?? 1
    control = (
      <div className="scale-control">
        <input id={inputId} type="range" min={definition.min} max={definition.max} value={numericValue} onChange={(event) => onChange(Number(event.target.value))} aria-describedby={describedBy} />
        <output htmlFor={inputId}>{response ? numericValue : 'Move to answer'}</output>
      </div>
    )
  } else {
    control = <input id={inputId} type="text" value={String(value)} maxLength={definition.maxLength} onChange={(event) => onChange(event.target.value)} aria-describedby={describedBy} />
  }

  return (
    <section className={error ? 'onboarding-field has-error' : 'onboarding-field'}>
      <div className="field-heading">
        <div>
          <span className="field-source">{definition.provenance.replaceAll('_', ' ')}</span>
          <h3 id={`${inputId}-label`}>{definition.label}</h3>
        </div>
        <span className={definition.required ? 'required-pill' : 'optional-pill'}>{definition.required ? 'Required' : 'Optional'}</span>
      </div>
      <label className="field-prompt" htmlFor={definition.kind === 'boolean' ? undefined : inputId}>{definition.prompt}</label>
      {definition.help && <p id={`${inputId}-help`} className="field-help">{definition.help}</p>}
      {definition.supportsAnswerState && (
        <fieldset className="answer-state-control">
          <legend>What is honest about your access to this answer right now?</legend>
          <p>Choosing uncertainty gives the app more truth than performing the answer you think a man should give.</p>
          <div className="answer-state-grid">
            {ONBOARDING_ANSWER_STATE_OPTIONS.map((option) => (
              <label className={answerState === option.value ? 'answer-state-option selected' : 'answer-state-option'} key={option.value}>
                <input type="radio" name={`${inputId}-answer-state`} checked={answerState === option.value} onChange={() => onAnswerStateChange(option.value as OnboardingAnswerState)} />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
      {definition.examples && (
        <details className="answer-examples">
          <summary>See three honest example answers</summary>
          <p className="example-boundary">Use these for recognition, not as an answer to copy.</p>
          <div className="example-answer-grid">
            {definition.examples.map((example) => (
              <article key={example.stage}>
                <span>{example.stage}</span>
                <h4>{example.title}</h4>
                <p>“{example.answer}”</p>
              </article>
            ))}
          </div>
        </details>
      )}
      {control}
      {definition.maxLength && typeof value === 'string' && <small className="character-count">{value.length}/{definition.maxLength}</small>}
      {error && <p id={`${inputId}-error`} className="field-error" role="alert">{error}</p>}
      {definition.sensitivity === 'high' ? (
        <p className="ai-use-note private">Kept in the restricted safety store and never sent to AI.</p>
      ) : (
        <label className="ai-use-control">
          <input type="checkbox" checked={response?.shareWithAi ?? definition.aiUseDefault} onChange={(event) => onAiUseChange(event.target.checked)} />
          Allow this answer to inform me through personalisation and better understanding of the man you are. This will be kept private.
        </label>
      )}
    </section>
  )
}

export function OnboardingWorkspace({ repository, onComplete }: OnboardingWorkspaceProps) {
  const [session, setSession] = useState<OnboardingSession | null>(null)
  const [responses, setResponses] = useState<Record<string, OnboardingResponse>>({})
  const [chapterIndex, setChapterIndex] = useState(0)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')

  useEffect(() => {
    let active = true
    void repository.getOnboardingSession().then((loaded) => {
      if (!active) return
      if (loaded.state === 'submitted') {
        onComplete()
        return
      }
      setSession(loaded)
      setResponses(loaded.responses)
      const index = ONBOARDING_CHAPTERS.findIndex((chapter) => chapter.id === loaded.currentChapterId)
      const legacyDraft = loaded.schemaVersion === 'pmm-onboarding-1'
      setChapterIndex(legacyDraft ? 0 : Math.max(0, index))
      if (legacyDraft) setStatus('Your saved draft is intact. Begin with the new orientation, then continue with your existing answers.')
    }).catch(() => setStatus('Your saved baseline could not be loaded. Please try again.'))
    return () => { active = false }
  }, [onComplete, repository])

  const chapter = ONBOARDING_CHAPTERS[chapterIndex]
  const chapterFields = useMemo(() => chapter ? fieldsForChapter(chapter.id, responses) : [], [chapter, responses])
  const progress = Math.round(((chapterIndex + 1) / ONBOARDING_CHAPTERS.length) * 100)
  const safetyTier = classifyOnboardingSafety(responses)
  const aiPreview = onboardingAiPreview(responses)

  function updateValue(definition: OnboardingFieldDefinition, value: OnboardingValue) {
    setResponses((current) => ({
      ...current,
      [definition.id]: createOnboardingResponse(definition, value, current[definition.id]?.shareWithAi ?? definition.aiUseDefault),
    }))
    setErrors((current) => {
      const next = { ...current }
      delete next[definition.id]
      return next
    })
  }

  function updateAnswerState(definition: OnboardingFieldDefinition, answerState: OnboardingAnswerState) {
    setResponses((current) => {
      const existing = current[definition.id]
      const value = existing?.value ?? ''
      return {
        ...current,
        [definition.id]: createOnboardingResponse(definition, value, existing?.shareWithAi ?? definition.aiUseDefault, answerState),
      }
    })
    setErrors((current) => {
      const next = { ...current }
      delete next[definition.id]
      return next
    })
  }

  function updateAiUse(definition: OnboardingFieldDefinition, enabled: boolean) {
    setResponses((current) => {
      const existing = current[definition.id]
      if (!existing) return current
      return { ...current, [definition.id]: { ...existing, shareWithAi: enabled && definition.sensitivity !== 'high' } }
    })
  }

  async function persist(nextIndex: number, completeCurrent = true) {
    if (!session || !chapter) return
    const validation = completeCurrent ? validateChapter(chapter.id, responses) : {}
    if (Object.keys(validation).length > 0) {
      setErrors(validation)
      document.getElementById(`onboarding-${Object.keys(validation)[0].replaceAll('.', '-')}`)?.focus()
      return
    }

    setSaving(true)
    setStatus('')
    try {
      const nextChapter = ONBOARDING_CHAPTERS[Math.max(0, Math.min(nextIndex, ONBOARDING_CHAPTERS.length - 1))]
      const saved = await repository.saveOnboardingChapter({
        sessionId: session.sessionId,
        expectedRevision: session.revision,
        chapterId: chapter.id,
        markComplete: completeCurrent,
        responses: chapterFields.flatMap((definition) => responses[definition.id] ? [responses[definition.id]] : []),
        nextChapterId: nextChapter.id,
      })
      setSession(saved)
      setResponses(saved.responses)
      setChapterIndex(nextIndex)
      setStatus(completeCurrent ? 'Saved privately.' : 'Draft saved. You can safely leave and return.')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      setStatus(onboardingPersistenceErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  async function submit() {
    if (!session || !chapter) return
    const validation = validateChapter(chapter.id, responses)
    if (Object.keys(validation).length > 0) {
      setErrors(validation)
      return
    }
    setSaving(true)
    setStatus('')
    try {
      const saved = await repository.saveOnboardingChapter({
        sessionId: session.sessionId,
        expectedRevision: session.revision,
        chapterId: chapter.id,
        markComplete: true,
        responses: chapterFields.flatMap((definition) => responses[definition.id] ? [responses[definition.id]] : []),
        nextChapterId: chapter.id,
      })
      const result = await repository.submitOnboarding(saved.sessionId, saved.revision)
      setSession(result.session)
      onComplete()
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Your baseline could not be submitted.')
    } finally {
      setSaving(false)
    }
  }

  if (!session || !chapter) return <main className="onboarding-loading"><span /><p>Opening your private baseline...</p></main>

  return (
    <main className="onboarding-shell">
      <aside className="onboarding-rail" aria-label="Questionnaire chapters">
        <p className="eyebrow">Private baseline · pause without performing</p>
        <h1>Tell the truth—even when the truth is “I don’t know yet.”</h1>
        <p>You are not being tested on how self-aware a man should be. A named uncertainty is useful context. An untouched blank stays untouched. Privacy is respected without being interpreted.</p>
        <div className="progress-track" aria-label={`${progress}% complete`}><span style={{ width: `${progress}%` }} /></div>
        <ol>
          {ONBOARDING_CHAPTERS.map((item, index) => (
            <li className={index === chapterIndex ? 'active' : session.completedChapterIds.includes(item.id) ? 'complete' : ''} key={item.id}>
              <button type="button" disabled={index > chapterIndex && !session.completedChapterIds.includes(item.id)} onClick={() => setChapterIndex(index)}>
                <span>{session.completedChapterIds.includes(item.id) ? '✓' : item.number}</span>{item.title}
              </button>
            </li>
          ))}
        </ol>
      </aside>

      <div className="onboarding-main">
        <header className="onboarding-chapter-header">
          <div><p className="eyebrow">Chapter {chapter.number} of {ONBOARDING_CHAPTERS.length - 1}</p><h2>{chapter.title}</h2><p>{chapter.description}</p></div>
          <span className="schema-pill">Baseline v2</span>
        </header>

        {chapter.id === 'orientation' && (
          <section className="onboarding-orientation-story">
            <p className="eyebrow">A note from Koen</p>
            <h3>{ONBOARDING_INTRO_STORY.title}</h3>
            {ONBOARDING_INTRO_STORY.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            <blockquote>{ONBOARDING_INTRO_STORY.closing}</blockquote>
            <aside>
              <strong>Why this matters for your plan</strong>
              <p>I can support “never explored,” “felt but unnamed,” “inherited,” “overloaded,” “private,” and “not applicable” differently. It gives me more understanding of where you are than an invented answer on what you think the answer should be. It is not allowed to fill those gaps with a guessed identity.</p>
            </aside>
          </section>
        )}

        {chapter.id === 'partner_knowledge' && (
          <>
            <PartnerConnectionCard />
            <PartnerWhatsAppDraft
              relationshipStatus={String(responses['profile.relationshipStatus']?.value ?? '')}
              childrenCount={Number(responses['children.count']?.value ?? 0)}
            />
          </>
        )}

        {safetyTier !== 'standard' && chapter.id !== 'review' && (
          <section className={`safety-route ${safetyTier}`} role="alert">
            <strong>{safetyTier === 'urgent' ? 'Pause the relationship work.' : 'Use extra care with relationship exercises.'}</strong>
            <p>{safetyTier === 'urgent'
              ? 'If danger is immediate, contact local emergency services or a trusted safe person now. The app will not contact anyone for you.'
              : 'The app will avoid confrontation prompts and prioritise private, specialist-informed support and safety planning.'}</p>
          </section>
        )}

        {chapter.id === 'review' && (
          <section className="onboarding-review">
            <div className="review-card"><strong>{Object.keys(responses).length}</strong><span>questions handled</span></div>
            <div className="review-card"><strong>{aiPreview.uncertaintyCount}</strong><span>honest unknowns named</span></div>
            <div className="review-card"><strong>{aiPreview.privateOrNotApplicableCount}</strong><span>private or not applicable</span></div>
            <div className="review-card"><strong>{aiPreview.included.length}</strong><span>answers eligible for AI</span></div>
            <div className="review-card"><strong>{aiPreview.excluded.length}</strong><span>answers kept out of AI</span></div>
            <div className={`review-card safety-${safetyTier}`}><strong>{safetyTier}</strong><span>safety route</span></div>
            <details>
              <summary>Review source-labelled answers and AI eligibility</summary>
              <div className="fact-review-list">
                {[...aiPreview.included, ...aiPreview.excluded].map((fact) => (
                  <article key={fact.factId}>
                    <div><strong>{fact.label}</strong><small>{fact.sourceLabel}</small></div>
                    <span className={fact.eligibleForAi ? 'ai-included' : 'ai-excluded'}>{fact.eligibleForAi ? 'AI eligible' : 'Not sent to AI'}</span>
                  </article>
                ))}
              </div>
            </details>
            {aiPreview.reflectionStates.length > 0 && (
              <details>
                <summary>Review how uncertainty and access were labelled</summary>
                <div className="fact-review-list">
                  {aiPreview.reflectionStates.map((item) => (
                    <article key={item.fieldId}>
                      <div><strong>{item.label}</strong><small>{item.sourceLabel}</small></div>
                      <span className={item.eligibleForAi ? 'ai-included' : 'ai-excluded'}>{item.answerState.replaceAll('_', ' ')}</span>
                    </article>
                  ))}
                </div>
              </details>
            )}
            <p className="review-boundary">Submitting creates one immutable, source-labelled baseline snapshot. Honest unknowns remain explicit instead of being guessed. It prepares a coach-reviewed generation request; it does not diagnose you or your partner, automatically contact anyone, or publish an AI answer directly to your daily practice.</p>
          </section>
        )}

        <div className="onboarding-fields">
          {chapterFields.map((definition) => (
            <FieldControl
              definition={definition}
              response={responses[definition.id]}
              error={errors[definition.id]}
              onChange={(value) => updateValue(definition, value)}
              onAiUseChange={(enabled) => updateAiUse(definition, enabled)}
              onAnswerStateChange={(answerState) => updateAnswerState(definition, answerState)}
              key={definition.id}
            />
          ))}
        </div>

        <footer className="onboarding-actions">
          <button className="secondary" type="button" disabled={saving || chapterIndex === 0} onClick={() => void persist(chapterIndex - 1, false)}>Previous</button>
          <button className="quiet" type="button" disabled={saving} onClick={() => void persist(chapterIndex, false)}>Save & leave</button>
          {chapter.id === 'review'
            ? <button className="primary" type="button" disabled={saving} onClick={() => void submit()}>{saving ? 'Creating baseline…' : 'Create my starting baseline'}</button>
            : <button className="primary" type="button" disabled={saving} onClick={() => void persist(chapterIndex + 1)}>{saving ? 'Saving…' : 'Save & continue'}</button>}
        </footer>
        {status && <p className="onboarding-status" role="status">{status}</p>}
      </div>
    </main>
  )
}
